import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setTotalAmount,
  setTaxSlab,
  setVatType,
  setVatValue,
  resetVatData,
} from "../redux/calculations/vatSlice";
import { updatedVatChart } from "../redux/calculations/calculaterSlice";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import DecimalUtils from '../utiles/decimalFormate';

const Vat = ({ setCalcDone }) => {
  const dispatch = useDispatch();
  const { totalAmount, taxSlab, vatType } = useSelector(
    (state) => state.vatCalculator
  );
  const { textData } = useLocale();
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const { vatCalc } = textData;

  const [selectedOption, setSelectedOption] = useState("excluding");

  const formik = useFormik({
    initialValues: {
      totalAmount: totalAmount,
      taxSlab: taxSlab,
      vatType: vatType,
    },
    validationSchema: Yup.object({
      totalAmount: Yup.number()
        .min(5000, vatCalc.totalAmountMin)
        .max(1000000, vatCalc.totalAmountMax)
        .required(vatCalc.totalAmountRequired),
      taxSlab: Yup.number()
        .required(vatCalc.vatRequired)
        .min(0.25, vatCalc.vatMin)
        .max(28, vatCalc.vatMax),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_VAT");
      }

      const amount = parseFloat(values.totalAmount);
      const taxRate = parseFloat(values.taxSlab) / 100;

      let vatAmount, principleAmount, totalWithVat;

      if (values.vatType === "excluding") {
        principleAmount = amount;
        vatAmount = amount * taxRate;
        totalWithVat = amount + vatAmount;
      } else {
        principleAmount = amount / (1 + taxRate);
        vatAmount = amount - principleAmount;
        totalWithVat = amount;
      }

      // Update Redux state
      dispatch(setTotalAmount(values.totalAmount));
      dispatch(setTaxSlab(values.taxSlab));
      dispatch(setVatType(values.vatType));
      dispatch(setVatValue(vatAmount));

      dispatch(
        updatedVatChart({
          totalPrinciple: principleAmount,
          vatAmmount: vatAmount,
          totalAmount: totalWithVat,
        })
      );      
      if (setCalcDone) {
        setCalcDone(true);
      }
    },
  });

     useEffect(() => {
        return () => {
          dispatch(resetVatData());
    
        };
      }, [dispatch]);

      const isRTL = () => {
        const rtlLanguages = ["ar", "fa", "ur"];
        return rtlLanguages.includes(i18n.language);
      };
    
  useEffect(() => {
    dispatch(setTotalAmount(formik.values.totalAmount));
    dispatch(setTaxSlab(formik.values.taxSlab));
    dispatch(setVatType(formik.values.vatType));
  }, [formik.values, dispatch]);

  const sliderRef = useRef(null);
  const vatSliderRef = useRef(null);

  const handleTotalAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    formik.setFieldValue("totalAmount", value);
    setCalcDone(false);
  };

  const handleIntrestRatetSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTaxSlab(value));
    formik.setFieldValue("taxSlab", value);
    setCalcDone(false);
  };

  const handleVatTypeChange = (type) => {
    setSelectedOption(type);
    formik.setFieldValue("vatType", type);
    setCalcDone(false);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";
  
    const shouldForceIndianFormat = i18n.language === "fa";
  
    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
          shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
        );
  };
  
  const handleAmountChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("totalAmount", "");
      return;
    }
    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 6) return;

    if (rawValue === "0") {
      formik.setFieldValue("totalAmount", 0);
      return;
    }
    formik.setFieldValue("totalAmount", rawValue ? Number(rawValue) : "");
    setCalcDone(false);
  };
  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
     if (rawValue === "") {
      formik.setFieldValue("taxSlab", rawValue);
      dispatch(setTaxSlab(""));
      return;
    }
     if (rawValue.length > 4) return;
     if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);
    dispatch(setTaxSlab(normalizedValue));
     formik.setFieldValue("taxSlab", normalizedValue);
     setCalcDone(false);
  };

  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  
  return (
    <div className="flex flex-col md:flex-row w-full gap-6">
      {/* Left side - Calculator Form */}
      <div className="w-full  pb-6">
        <div className="flex items-center my-6 gap-5 sm:gap-8">
          {/* Excluding VAT */}
          <div className="flex items-center">
            <label
              htmlFor="excluding-vat"
              className="flex items-center gap-1 sm:gap-[10px] text-black text-sm md:text-sm font-normal"
            >
              <input
                type="radio"
                id="excluding-vat"
                name="vat"
                value="excluding"
                checked={selectedOption === "excluding"}
                onChange={() => handleVatTypeChange("excluding")}
                className="sr-only"
              />
              <div
                className={`h-5 w-5 rounded-full border ${
                  selectedOption === "excluding"
                    ? "border-[#07B5B5] bg-white"
                    : "border-[#07B5B5]"
                } flex items-center justify-center`}
              >
                {selectedOption === "excluding" && (
                  <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                )}
              </div>
              <span>{textData.vatCalc.excludingVatLabel}</span>
            </label>
          </div>

          {/* Including VAT */}
          <div className="flex items-center">
            <label
              htmlFor="including-vat"
              className="gap-1 sm:gap-[10px] items-center flex text-black  text-sm sm:text-sm  font-normal"
            >
              <input
                type="radio"
                id="including-vat"
                name="vat"
                value="including"
                checked={selectedOption === "including"}
                onChange={() => handleVatTypeChange("including")}
                className="sr-only"
              />
              <div
                className={`h-5 w-5 rounded-full border ${
                  selectedOption === "excluding"
                    ? "border-[#07B5B5] bg-white"
                    : "border-[#07B5B5]"
                } flex items-center justify-center`}
              >
                {selectedOption === "including" && (
                  <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                )}
              </div>
              <span> {textData.vatCalc.includingVatLabel}</span>{" "}
            </label>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
          <div >
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.vatCalc.totalAmountLabel}
              </label>
             
            </div><div className="flex justify-between gap-5 items-center"> 

            <div className="relative   w-full " ref={sliderRef}>
              {/* Background track */}
              
              <input
                type="range"
                min={5000}
                max={1000000}
                step={1000}
                value={formik.values.totalAmount}
                onChange={handleTotalAmountSliderChange}
                className="flex-1 min-w-0 max-w-[314px] appearance-none bg-transparent"
                style={{
                  width: "100%",
                  appearance: "none",
                  background: "transparent",...(isRTL() && {
                    direction: 'ltr',
                  }),
                  "--val": `${
                    ((formik.values.totalAmount - 5000) / (1000000 - 5000)) * 100
                  }%`,
                }}
              />
            </div> <div className="flex items-center flex-col">
                <Input
                  name="totalAmount"
                  leftIcon={textData.currency}
                  value={formatToINR(formik.values.totalAmount)}
                  onChange={handleAmountChange}
                  onBlur={formik.handleBlur}
                  type="text"
                    dir="ltr"
                  placeholder={getPlaceholderValue(i18n.language)} 
                  borderColor={
                    formik.touched.totalAmount && formik.errors.totalAmount
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                      formik.touched.totalAmount && formik.errors.totalAmount
                        ? "border-danger"
                        : "border-gray-light"
                    } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.totalAmount && formik.errors.totalAmount && (
                  <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                    {formik.errors.totalAmount}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div >
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.vatCalc.taxslab}
              </label>
            
            </div>
            <div className="flex justify-between gap-5 items-center"> 

            <div className="relative   w-full " ref={vatSliderRef}>
              {/* Background track */}
              
              <input
                type="range"
                min={0.25}
                max={28}
                step={0.1}
                value={parseFloat(formik.values.taxSlab)}
                onChange={handleIntrestRatetSliderChange}
                className="flex-1 min-w-0 max-w-[314px]"
                style={{
                  width: "100%",
                  appearance: "none",
                  background: "transparent",...(isRTL() && {
                    direction: 'ltr',
                  }),
                  "--val": `${((taxSlab - 0.25) / 28) * 100}%`,
                }}
              />
              </div> 
               <div className="flex items-center flex-col">
                <Input
                  name="taxSlab"
                  leftIcon="%"
                    type="text"
                  value={DecimalUtils.formatDisplay(formik.values.taxSlab)}
                  onChange={handleInterestRateChange}
                  onBlur={formik.handleBlur}
                  placeholder={DecimalUtils.getPlaceholder("12")}

                  borderColor={
                    formik.touched.taxSlab && formik.errors.taxSlab
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                      formik.touched.taxSlab && formik.errors.taxSlab
                        ? "border-danger"
                        : "border-gray-light"
                    } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.taxSlab && formik.errors.taxSlab && (
                  <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                    {formik.errors.taxSlab}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-[10px]">
            <Button type="submit" className="w-full text-white">
              {textData.calculate}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Vat;
